import * as web3 from "@solana/web3.js"
import { useState, useEffect, CSSProperties } from 'react'
import { useParams } from 'react-router-dom'
import { AdCampaign } from '../models/AdCampaign';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useProgram } from '../utils/ProgramContext';
import bs58 from "bs58";
import { Provider } from "../models/Provider";
import { AuctionRoom } from "../models/AuctionRoom";
import { Auction } from "../models/Auction";
import BN from "bn.js";

const styles: { [key: string]: CSSProperties } = {
	list: {
		padding: 0,
		listStyleType: 'none',
	},
	listItem: {
		margin: '10px 0',
		padding: '15px',
		borderRadius: '8px',
		boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
		border: '1px solid rgba(0, 0, 0, 0.1)',
		backgroundColor: '#fff',
	},
	name: {
		fontWeight: 'bold',
		marginBottom: '10px',
	},
	checkbox: {
		marginRight: '10px',
	},
};

function getTimestampInSeconds() {
	return Math.floor(Date.now() / 1000)
}

const BidAuction = () => {

	const { id } = useParams();

	const [adCampaign, setAdCampaing] = useState<AdCampaign>()
	const [providerAuctionsDict, setProviderAuctionsDict] = useState<{ [key: string]: Auction[] }>({})
	const [providerCertificationDict, setProviderCertificationDict] = useState<{ [key: string]: web3.PublicKey }>({})
	const [providers, setProviders] = useState<Provider[]>([])
	const { connection } = useConnection()

	const { program } = useProgram() || {};
	const { publicKey, sendTransaction } = useWallet();


	useEffect(() => {

		async function fetchAdCampaign() {
			if (!program || !id || !publicKey) { return }

			const [adCampaignPda] = await AdCampaign.findAddress(publicKey, parseInt(id));

			const account = await program.account.adCampaign.fetch(adCampaignPda);
			if (!account) { return }
			setAdCampaing(new AdCampaign(account.id as number, account.name as string, account.link as string, adCampaignPda as web3.PublicKey))
		}

		fetchAdCampaign()

	}, [program, id, publicKey])


	useEffect(() => {

		async function fetchRelations() {

			if (!program || !adCampaign || !adCampaign.publicKey) { return }
			const res = await program.account.providerAdCampaignRelation.all([
				{
					memcmp: {
						offset: 8 + 32,
						bytes: adCampaign.publicKey.toBase58()
					}
				},
				{
					memcmp: {
						offset: 8 + 32 + 32,
						bytes: bs58.encode([1])
					}
				},
			])

			const providerCertificationDictTemp: any = {}
			for (const relation of res) {
				providerCertificationDictTemp[(relation.account.provider as web3.PublicKey).toString()] = relation.publicKey
			}
			setProviderCertificationDict(providerCertificationDictTemp)

			const providerPdas = res.map(relation => relation.account.provider as web3.PublicKey)

			const accountsPromises = [];
			for (const providerPda of providerPdas) {

				const promiseWithPda = program.account.provider.fetch(providerPda).then(account => {
					return { account, providerPda };
				});
				accountsPromises.push(promiseWithPda);
			}

			const results = await Promise.all(accountsPromises);

			if (results.length == 0) {
				return;
			}

			const tempProviders = [];
			for (const { account, providerPda } of results) {
				if (account) {
					tempProviders.push(new Provider(account.id as number, account.name as string, account.location as string, providerPda));
				}
			}
			setProviders(tempProviders)
		}
		fetchRelations();

	}, [program, adCampaign])


	useEffect(() => {


		async function fetchAuctions() {
			let auctionRooms: any = []

			const auctionRoomPromises: any = []
			providers.forEach((provider) => {
				const providerPda = provider.publicKey as web3.PublicKey;
				const [auctionRoomPda] = AuctionRoom.findAddress(providerPda);
				const auctionRoomPromisesWithPda = program?.account.auctionRoom.fetch(auctionRoomPda).then((account) => ({ providerPda, account }));
				auctionRoomPromises.push(auctionRoomPromisesWithPda)
			})

			auctionRooms = await Promise.all(auctionRoomPromises);

			if (auctionRooms.length == 0) { return }

			const tempProviderAuctionRoomRelation: { [key: string]: AuctionRoom } = {};
			for (const { account, providerPda } of auctionRooms) {
				tempProviderAuctionRoomRelation[providerPda.toString()] = new AuctionRoom(providerPda, (account.timeDenomination as BN).toNumber());
			}

			const tempProviderAuctionDict: { [key: string]: Auction[] } = {}
			for (const provider of providers) {
				if (!program || Object.keys(tempProviderAuctionRoomRelation).length == 0) { return }
				const providerPda = provider.publicKey as web3.PublicKey;
				const auctionRoom = tempProviderAuctionRoomRelation[providerPda.toString()]

				let startTime: number = Math.floor(getTimestampInSeconds() / auctionRoom.timeDenomination) * auctionRoom.timeDenomination;
				const auctionList: Auction[] = []
				let auctionAccount = null;
				let nextAuctionPda = null;
				let keepLooking = true;


				while (keepLooking) {
					[nextAuctionPda] = Auction.findAddress(providerPda, startTime);
					try {
						auctionAccount = await program.account.auction.fetch(nextAuctionPda);
						auctionList.push(
							new Auction(
								nextAuctionPda,
								providerPda,
								(auctionAccount.startTime as BN).toNumber(),
								(auctionAccount.startingPrice as BN).toNumber(),
								(auctionAccount.minBidDelta as BN).toNumber(),
								(auctionAccount.expiration as BN).toNumber(),
								(auctionAccount.currentPrice as BN).toNumber(),
								auctionAccount.active as boolean,
								auctionAccount.reward as boolean,
								auctionAccount.highestBidder as web3.PublicKey
							)
						)
						startTime += auctionRoom.timeDenomination;
					}
					catch (e) {
						keepLooking = false
						console.log(e)
						console.log("No more auctions")
					}
				}
				tempProviderAuctionDict[providerPda.toString()] = auctionList
			}

			setProviderAuctionsDict(tempProviderAuctionDict)
		}

		fetchAuctions()

	}, [providers])


	const handleBidMinimum = async (event: any, auction: Auction, provider: Provider) => {

		event.preventDefault()

		if (!publicKey || !program || !adCampaign || !provider.publicKey) {
			return;
		}


		const transaction = await program.methods.createBid(
			adCampaign.id, provider.publicKey, new BN(auction.startTime), new BN(auction.currentPrice + auction.minBidDelta + 1000)
		).accounts({
			auction: auction.pubkey,
			prevBidder: auction.highestBidder || adCampaign.publicKey,
			provierAdCampaignRelation: providerCertificationDict[provider.publicKey.toString()] as web3.PublicKey,
			adCampaign: adCampaign.publicKey,
			authority: publicKey,
			systemProgram: web3.SystemProgram.programId,
		}).transaction()

		try {
			const txid = await sendTransaction(transaction, connection);
			alert(
				`Transaction submitted: https://explorer.solana.com/tx/${txid}?cluster=devnet`
			);
			console.log(
				`Transaction submitted: https://explorer.solana.com/tx/${txid}?cluster=devnet`
			);
		} catch (e) {
			console.log(JSON.stringify(e));
			alert(JSON.stringify(e));
		}
	}


	return (
		<main className="flex flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-6">
			<div className="flex items-center">
				<h1 className="text-lg font-semibold md:text-2xl">Bid on Auctions</h1>
			</div>
			<div className="flex flex-1 items-center justify-center rounded-lg border border-dashed shadow-sm">
				<div className="flex flex-col items-center gap-1 text-center">
					<div className="screen-provider-list">
						<ul style={styles.list}>
							{providers.map((provider, index) => (
								<li key={index} style={styles.listItem}>
									<div style={styles.name}>Name: {provider.name}</div>
									<div style={styles.name}>Location: <a href={provider.location} target="_blank" rel="noreferrer">{provider.location}</a></div>
									<div style={styles.name}>PublicKey: {provider.publicKey?.toString()}</div>
									<ul style={styles.list}>
										{
											provider.publicKey &&
											provider.publicKey.toString() in providerAuctionsDict &&
											providerAuctionsDict[provider.publicKey.toString()]
												.map((auction: Auction, index: React.Key) => {
													return <li key={index} style={styles.listItem}>
														<div style={styles.name}>PubKey: {auction.pubkey?.toString()}</div>
														<div style={styles.name}>Start: {new Date(auction.startTime * 1000).toLocaleTimeString('en-US')}</div>
														<div style={styles.name}>Expiration: {new Date(auction.expiration * 1000).toLocaleTimeString('en-US')}</div>
														<div style={styles.name}>Starting Price: {auction.startPrice / 10 ** 9} SOL</div>
														<div style={styles.name}>Minimum Delta: {auction.minBidDelta / 10 ** 9} SOL</div>
														<div style={styles.name}>Current Price: {auction.currentPrice ? (auction.currentPrice / 10 ** 9) : 0}  SOL</div>
														<button className={"m-5 bg-black hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"} onClick={(event) => handleBidMinimum(event, auction, provider)}>Bid Minimum</button>
													</li>

												})
										}
									</ul>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</main>

	)


}



export default BidAuction;